import "./index.scss";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  Typography,
  Stack,
  Box,
  DialogTitle,
  IconButton,
} from "@mui/material";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import ApiService from "../../services/api-service";
import { isProductionEnvironment } from "../../utils/urlUtils";
import { sikiCashoutLimits } from "./siki-cashout-limits";

const loadSikiCashoutLimits = async () => {
  const response = await ApiService.CHARGES();
  return sikiCashoutLimits({
    isProd: isProductionEnvironment(),
    minimumCashout: response["result"]?.minimumCashout,
  });
};

export default function OffboardingBox(props) {
  const { t } = useTranslation("common");
  const [amount, setAmount] = useState("");
  const [paypalAddress, setPaypalAddress] = useState("");
  const [minAmount, setMinAmount] = useState(0);
  const [maxAmount, setMaxAmount] = useState(0);

  useEffect(() => {
    if (!props.open) {
      return undefined;
    }

    setAmount("");
    setPaypalAddress("");

    let cancelled = false;
    (async () => {
      try {
        const limits = await loadSikiCashoutLimits();
        if (!cancelled) {
          setMinAmount(limits.minAmount);
          setMaxAmount(limits.maxAmount);
        }
      } catch (_error) {
        return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [props.open]);

  const resetAndClose = () => {
    props.handleClose();
    setAmount("");
    setPaypalAddress("");
  };

  const boxTitle = t("sikiOffboardingBox.title");
  const amountLabel = t("sikiOffboardingBox.amountLabel");
  const addressLabel = t("paypalAddressLabel");
  const minMsg = t("sikiOffboardingBox.minAmount", { tokenAmount: minAmount });
  const minMsgShort = t("sikiOffboardingBox.minAmountShort", {
    tokenAmount: minAmount,
  });
  const maxMsg = t("sikiOffboardingBox.maxAmount", { tokenAmount: maxAmount });
  const txProceed = t("proceed");

  return (
    <div>
      <Dialog
        open={props.open}
        keepMounted
        onClose={resetAndClose}
        PaperProps={{
          style: {
            position: "relative",
            backgroundColor: "#090909",
            boxShadow: "none",
            color: "#000000",
            borderRadius: 16,
            paddingBottom: "16px",
            backgroundImage: "none",
            width: "300px",
          },
        }}
        aria-describedby="alert-dialog-slide-description"
      >
        <Stack spacing={1}>
          <DialogTitle
            color="default"
            sx={{
              padding: "16px",
              color: "#ffffff",
              fontSize: "20px",
              borderBottom: "1px solid #1E1E1E",
            }}
          >
            {boxTitle}
            <IconButton
              aria-label="close"
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
              onClick={resetAndClose}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Stack sx={{ padding: "16px", borderBottom: "1px solid #1E1E1E" }}>
            <Typography className="fs-14" mb="5px !important">
              {amountLabel}
            </Typography>
            <DialogContent sx={{ padding: 0 }}>
              <Box
                style={{
                  borderRadius: "25px",
                  border: "1px solid gray",
                  padding: "16.5px 14px",
                  backgroundColor: "#1E1E1E",
                }}
              >
                <Stack direction={"row"}>
                  <input
                    value={amount}
                    type="number"
                    onChange={(e) => setAmount(e.target.value)}
                    onKeyDown={(e) =>
                      ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()
                    }
                    style={{
                      border: "none",
                      minWidth: "200px",
                      width: "90%",
                      backgroundColor: "transparent",
                      color: "#ffffff",
                    }}
                    placeholder={minMsgShort}
                    autoFocus
                  />
                </Stack>
              </Box>
            </DialogContent>
            {amount && amount < minAmount ? (
              <Typography
                variant="body2"
                sx={{ fontSize: "12px" }}
                mt={1}
                color="error"
              >
                {minMsg}
              </Typography>
            ) : amount && amount > maxAmount ? (
              <Typography
                variant="body2"
                sx={{ fontSize: "12px" }}
                mt={1}
                color="error"
              >
                {maxMsg}
              </Typography>
            ) : null}
          </Stack>

          <Stack sx={{ padding: "16px", borderBottom: "1px solid #1E1E1E" }}>
            <Typography className="fs-14" mb="5px !important">
              {addressLabel}
            </Typography>
            <DialogContent sx={{ padding: 0 }}>
              <Box
                style={{
                  borderRadius: "25px",
                  border: "1px solid gray",
                  padding: "16.5px 14px",
                  backgroundColor: "#1E1E1E",
                }}
              >
                <Stack direction={"row"}>
                  <input
                    value={paypalAddress}
                    type="string"
                    onChange={(e) => setPaypalAddress(e.target.value)}
                    placeholder={""}
                    style={{
                      border: "none",
                      minWidth: "200px",
                      width: "90%",
                      backgroundColor: "transparent",
                      color: "#ffffff",
                    }}
                  />
                </Stack>
              </Box>
            </DialogContent>
          </Stack>

          <Stack
            justifyContent="center"
            sx={{
              width: "50%",
              margin: "auto !important",
              marginTop: "8px !important",
            }}
          >
            <Button
              onClick={() => {
                if (amount !== "") {
                  props.handleSubmit(amount, paypalAddress);
                }
              }}
              title={txProceed}
              sx={{
                height: "30px !important",
                fontWeight: "bold",
              }}
              variant="contained"
              color="primary"
            >
              {txProceed}
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </div>
  );
}
